import { MonedaOperacionField } from "./moneda-operacion-field";
import { Form, FormProps, useFormContext, useStore } from "@/components/form";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { getTotalMonto, getTotalTransferencia } from "@/features/monetizacion/shared/utils";
import { TransferenciaItemRequest, TransferenciaRequest } from "@/shared/types/transferencia";
import { Venta } from "@/shared/types/venta";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { Card, Stack, StackProps, Typography, Box } from "@mui/material";
import { ReactNode, useEffect } from "react";
import { z } from "zod/v4";
import { formatDate, formatString } from "@/shared/formats";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import PrecioView from "@/features/monetizacion/components/precio-view";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";

export const cajaPagoFormSchema = z.object({
    pago: z.object({
        items: z.array(z.object({
            monto: z.object({
                items: z.array(z.object({
                    monedaId: z.string().nonempty("Requerido"),
                    cantidad: z.number({ message: "Requerido" })
                }))
            }),
            transferenciaMetodoId: z.string().nonempty("Este campo es requerido")
        })).transform(v => v.filter(vi => vi.monto).map(vi => vi as TransferenciaItemRequest)),
        facturacionElectronica: z.boolean().default(false),
        monedaOperacionId: z.string().optional(),
        conversionGuarani: z.number().optional()
    }),
    vuelto: z.object({
        items: z.array(z.object({
            monto: z.object({
                items: z.array(z.object({
                    monedaId: z.string().nonempty("Requerido"),
                    cantidad: z.number({ message: "Requerido" })
                }))
            }),
            transferenciaMetodoId: z.string().nonempty("Este campo es requerido")
        })).transform(v => v.filter(vi => vi.monto).map(vi => vi as TransferenciaItemRequest))
    })
});

interface CajaPagoFormProps extends FormProps<z.input<typeof cajaPagoFormSchema>> {
    venta: Partial<Venta> & { total: number }
}

export default function CajaPagoForm({ venta, form, ...props }: CajaPagoFormProps) {
    const { transferenciaMetodos, cotizacion, monedaBase } = useMonetizacion();
    const { facturacionEnabled } = useOperationalMode();
    const totalAPagar = venta.total;

    // Estado y sincronización de moneda/conversión delegados al componente
    const monedaOperacionValue = {
        monedaOperacionId: form.getFieldValue("pago.monedaOperacionId") || monedaBase.id,
        conversionGuarani: form.getFieldValue("pago.conversionGuarani") ?? 1
    };

    const handleMonedaOperacionChange = (val: { monedaOperacionId: string; conversionGuarani: number }) => {
        form.setFieldValue("pago.monedaOperacionId", val.monedaOperacionId);
        form.setFieldValue("pago.conversionGuarani", val.conversionGuarani);
    };

    useEffect(() => {
        form.focusField("pago.items[0].monto", true)
    }, []);

    const handleAddNewPagoItem = (push: (ti: TransferenciaItemRequest) => void) => {
        const items = form.getFieldValue("pago.items");
        const existingMetodoIdList = items.map((ti: any) => ti.transferenciaMetodoId);
        const nextTransferenciaMetodoId = transferenciaMetodos.filter(tm => !existingMetodoIdList.includes(tm.id))[0].id;
        const totalMonto = items.reduce((prev: any, curr: any) => prev + (curr.monto ? getTotalMonto(curr.monto, cotizacion) : 0), 0);
        const totalRestante = Math.max(totalAPagar - totalMonto, 0);
        push({ monto: { items: [{ monedaId: monedaBase.id, cantidad: totalRestante }] }, transferenciaMetodoId: nextTransferenciaMetodoId })
    }

    const handleSetVuelto = () => {
        const totalPagado = getTotalTransferencia(form.getFieldValue("pago"), cotizacion);
        const vuelto = Math.max(totalPagado - totalAPagar, 0);
        form.setFieldValue("vuelto.items[0].monto", { items: [{ monedaId: monedaBase.id, cantidad: vuelto }] })
    }

    // El select y field deben reaccionar en tiempo real al valor del checkbox
    return <Form form={form} gap={3} {...props}>
        <Stack flex={1} gap={5} direction={{ xs: "column-reverse", md: "row" }} alignItems={{ xs: "stretch", md: "start" }}>
            <Stack flex={1} gap={3}>
                {/* Métodos de pago */}
                <form.Field name="pago.items" mode="array" listeners={{ onChange: handleSetVuelto }}>
                    {field => <Stack gap={2}>
                        <Typography variant="h6">Métodos de Pago</Typography>
                        {field.state.value.map((item: any, index: number) => <PagoFieldEntry
                            key={index}
                            index={index}
                            item={item}
                            totalAPagar={totalAPagar}
                            onChange={handleSetVuelto}
                            onRemoveClick={() => field.removeValue(index)}
                        />)}
                        {field.state.value.length < transferenciaMetodos.length && <Form.Button
                            startIcon={<AddIcon />}
                            size="small"
                            onClick={() => handleAddNewPagoItem(field.pushValue)}
                        >
                            Agregar Forma de Pago
                        </Form.Button>}
                        <PagoTotalByMoneda items={field.state.value} />
                    </Stack>}
                </form.Field>
                <Stack gap={2}>
                    <Typography variant="h6">Vuelto</Typography>
                    <VueltoFieldEntry totalAPagar={totalAPagar} />
                </Stack>
                {/* Checkbox de facturación electrónica y select/field dependientes */}
                {facturacionEnabled && !venta.credito && (
                    <form.Subscribe selector={state => state.values.pago.facturacionElectronica}>
                        {facturacionElectronica => (
                            <Stack gap={0.5}>
                                <Typography variant="h6">Facturación Electrónica</Typography>
                                <Form.CheckBox
                                    name="pago.facturacionElectronica"
                                    label="Facturación Electrónica"
                                    size="small"
                                />
                                {!!facturacionElectronica && (
                                    <Stack direction="row" gap={2} paddingTop={1} alignItems="center">
                                        <MonedaOperacionField
                                            value={monedaOperacionValue}
                                            onChange={handleMonedaOperacionChange}
                                        />
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </form.Subscribe>
                )}
            </Stack>
            <Stack gap={3}>
                <Card variant="outlined">
                    <Stack padding={2} gap={1} minWidth={"250px"}>
                        <Typography variant="h6">Datos de la Factura</Typography>
                        <Stack gap={1} paddingLeft={2}>
                            {venta.numeroVenta && <AttributeValuePair label="N° de Venta: " value={venta.numeroVenta} />}
                            {venta.fecha && <AttributeValuePair label="Fecha: " value={formatDate(venta.fecha)} />}
                            {venta.clienteNombre && <AttributeValuePair label="Cliente: " value={formatString(venta.clienteNombre)} />}
                            {venta.clienteNumDocumento && <AttributeValuePair label="RUC: " value={formatString(venta.clienteNumDocumento)} />}
                            {venta.vendedorNombre && <AttributeValuePair label="Vendedor: " value={formatString(venta.vendedorNombre)} />}
                        </Stack>
                    </Stack>
                </Card>
                <Summary totalAPagar={totalAPagar} />
            </Stack>
        </Stack>
    </Form>;
}


//#region private components
function AttributeValuePair({
    label,
    value
}: {
    label: ReactNode,
    value: ReactNode
}) {
    return <Stack direction={"row"} gap={3} justifyContent={"space-between"}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="body2">{!value && value !== 0 ? "---" : value}</Typography>
    </Stack>
}

interface PagoFieldEntryProps extends StackProps {
    index: number,
    item: TransferenciaItemRequest,
    totalAPagar: number,
    onChange?: () => void,
    onRemoveClick?: (e: React.MouseEvent) => void
}

function PagoFieldEntry({
    index,
    item,
    totalAPagar,
    onChange,
    onRemoveClick,
    ...props
}: PagoFieldEntryProps) {
    const form = useFormContext();
    const { transferenciaMetodos, cotizacion } = useMonetizacion();

    const pago: TransferenciaRequest = useStore(form.store, state => state.values.pago);
    const transferenciaMetodoId: string = useStore(form.store, state => state.values.pago.items[index].transferenciaMetodoId);

    const existingTransferenciaMetodoId = pago.items.map(ti => ti.transferenciaMetodoId);
    const selectableTransferenciaMetodos = transferenciaMetodos.filter(tm => !existingTransferenciaMetodoId.includes(tm.id) || tm.id == transferenciaMetodoId);

    const pagoSinEste = { items: pago.items.filter((_, i) => i !== index) };
    const pagadoSinEste = getTotalTransferencia(pagoSinEste, cotizacion);

    return <Stack gap={2} {...props}>
        <Stack direction={"row"} gap={2} alignItems={"center"}>
            <Stack flex={3}>
                <Form.Select name={`pago.items[${index}].transferenciaMetodoId`} label="Forma de Pago" size="small">
                    {selectableTransferenciaMetodos.map(tm => <Form.Select.Option key={tm.id} value={tm.id}>{tm.nombre}</Form.Select.Option>)}
                </Form.Select>
            </Stack>
            <Stack flex={4} minWidth={0}>
                <Form.MontoField
                    name={`pago.items[${index}].monto`}
                    label="Monto"
                    size="small"
                    registerOptions={{ listeners: { onChange: onChange } }}
                    objective={totalAPagar}
                    objectiveFilled={pagadoSinEste}
                />
            </Stack>
            <Form.IconButton onClick={onRemoveClick}><ClearIcon /></Form.IconButton>
        </Stack>
    </Stack>
}

interface VueltoFieldEntryProps extends StackProps {
    totalAPagar: number
}

function VueltoFieldEntry({
    totalAPagar,
    ...props
}: VueltoFieldEntryProps) {
    const { cotizacion } = useMonetizacion();
    const form = useFormContext();

    return <form.Subscribe selector={state => [state.values.pago, state.values.vuelto]}>
        {([transPagado]) => {
            const totalPagado = getTotalTransferencia(transPagado, cotizacion);
            const vuelto = Math.max(totalPagado - totalAPagar, 0);

            return <Stack direction={"row"} gap={2} alignItems={"center"} {...props}>
                <Stack flex={4}>
                    <Form.MontoField
                        name="vuelto.items[0].monto"
                        label="Vuelto"
                        size="small"
                        objective={vuelto}
                        emotionalDamage={true}
                    />
                </Stack>
            </Stack>
        }}
    </form.Subscribe>
}

function Summary({
    totalAPagar
}: {
    totalAPagar: number
}) {
    const openModal = useOpenModal();
    const { cotizacion } = useMonetizacion();
    const form = useFormContext();

    return <form.Subscribe selector={state => [state.values.pago, state.values.vuelto]}>
        {([transPagado, transVuelto]) => {
            const totalPagado = getTotalTransferencia(transPagado, cotizacion);
            const totalVuelto = getTotalTransferencia(transVuelto, cotizacion);
            const faltaPagar = Math.max(totalAPagar - totalPagado, 0);
            const vuelto = Math.max(totalPagado - totalAPagar, 0);
            const vueltoFaltante = faltaPagar > 0 ? 0 : vuelto - totalVuelto;

            const isFaltaPagarValid = Math.abs(faltaPagar) < 0.001
            const isVueltoFaltanteValid = Math.abs(vueltoFaltante) < 0.001

            return <>
                <Stack direction={"row"} gap={4} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography variant="h6" noWrap>Total a Pagar: </Typography>
                    <PrecioView variant="h6" monto={totalAPagar} />
                </Stack>
                <Stack direction={"row"} gap={4} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography variant="h6" noWrap>Falta Pagar: </Typography>
                    <PrecioView variant="h6" color={!isFaltaPagarValid ? "error" : undefined} monto={faltaPagar} />
                </Stack>
                <Stack direction={"row"} gap={4} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography variant="h6" noWrap>Vuelto: </Typography>
                    <PrecioView variant="h6" monto={vuelto} />
                </Stack>
                <Stack direction={"row"} gap={4} alignItems={"center"} justifyContent={"space-between"}>
                    <Typography variant="h6" noWrap>Vuelto Faltante: </Typography>
                    <PrecioView variant="h6" color={!isVueltoFaltanteValid ? "error" : undefined} monto={vueltoFaltante} />
                </Stack>
                <Form.SubmitButton
                    disabled={!isFaltaPagarValid || !isVueltoFaltanteValid}
                    onClick={e => {
                        e.preventDefault();
                        openModal("confirmPay");
                    }}
                >Confirmar</Form.SubmitButton>

                <ConfirmDialog code="confirmPay">
                    <ConfirmDialog.Content
                        title="Confirmar Pago"
                        description={`¿Estás seguro que quieres confirmar el pago?`}
                        onConfirm={async () => await form.handleSubmit()}
                    />
                </ConfirmDialog>
            </>
        }}
    </form.Subscribe>
}
//#endregion

function PagoTotalByMoneda({ items }: { items: TransferenciaItemRequest[] }) {
    const { monedas } = useMonetizacion();

    // Agrupar y sumar montos por moneda
    const monedaTotales = items.reduce((acc: Map<string, number>, item: any) => {
        if (item.monto?.items) {
            item.monto.items.forEach((montoItem: any) => {
                const current = acc.get(montoItem.monedaId) || 0;
                acc.set(montoItem.monedaId, current + montoItem.cantidad);
            });
        }
        return acc;
    }, new Map<string, number>());

    if (monedaTotales.size === 0) return null;

    return (
        <Stack gap={1} sx={{ marginTop: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Total por Moneda</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
                {Array.from(monedaTotales.entries()).map(([monedaId, cantidad]) => {
                    const moneda = monedas.find(m => m.id === monedaId);
                    return moneda ? (
                        <Box
                            key={monedaId}
                            component="fieldset"
                            sx={{
                                flex: 1, minWidth: 150,
                                border: "1px solid",
                                borderColor: "#b3b9c6",
                                borderRadius: 1,
                                padding: "4px 8px 6px",
                                margin: 0,
                                backgroundColor: "transparent"
                            }}
                        >
                            <Box component="legend" sx={{ fontSize: "0.75rem", color: "text.secondary", paddingX: 0.5 }}>
                                {moneda.nombre} ({moneda.simbolo})
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 400, textAlign: "center", width: "100%" }}>
                                {cantidad.toLocaleString('es-PY', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    ) : null;
                })}
            </Stack>
        </Stack>
    );
}